import React, { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { AlertTriangle, X, CheckCircle2, XCircle, Clock, User as UserIcon, Link2, FileText, Calendar, MessageSquare } from 'lucide-react';

/*
 * ReviewEntryDialog — read-only viewer for pending ledger / expense entries.
 *
 * NOTE ON LAYOUT SYNC:
 * Field labels, order, and grouping intentionally MIRROR the create forms in
 * `pages/Ledger.js` and `pages/Expenses.js`. When you change a create form,
 * mirror the change here — the two views must stay in sync visually.
 * (A future refactor could hoist the create-form JSX into a shared
 * `<LedgerEntryFields mode="review|create" />` — deferred to avoid touching
 * the create paths in this change.)
 *
 * This dialog performs ZERO side effects on mount beyond one optional
 * cycle-lookup fetch (needed to display the cycle window in provenance).
 * No live-rate fetches, no recomputes, no validation runs.
 */

const RATE_MIN = 60;
const RATE_MAX = 130;

const disabledInput = "form-input w-full";
const disabledInputStyle = { background: 'var(--cb-bone)', color: 'var(--cb-ink)', cursor: 'not-allowed', opacity: 0.9 };

function LabeledReadonly({ label, value, testid, className = '', mono = false }) {
  return (
    <div className={className}>
      <label className="form-label">{label}</label>
      <input
        type="text"
        readOnly
        disabled
        value={value ?? ''}
        data-testid={testid}
        className={disabledInput + (mono ? ' cb-num' : '')}
        style={disabledInputStyle}
      />
    </div>
  );
}

function ProvenancePanel({ entry, sibling, cycle, userMap }) {
  const created_by_name = userMap[entry.created_by] || entry.created_by || '—';
  const approved_by_name = entry.approved_by ? (userMap[entry.approved_by] || entry.approved_by) : null;
  const editHistory = entry.edit_history || [];
  return (
    <div className="cb-card" style={{ padding: 'var(--cb-space-4)', background: 'var(--cb-bone)' }} data-testid="review-provenance-panel">
      <h3 className="cb-eyebrow" style={{ marginBottom: 'var(--cb-space-3)' }}>Provenance</h3>
      <div className="space-y-3" style={{ fontSize: '0.8125rem' }}>
        <ProvRow icon={UserIcon} label="Created by" value={<span data-testid="review-created-by">{created_by_name}</span>} />
        <ProvRow icon={Calendar} label="Created at" value={<span className="cb-num" data-testid="review-created-at">{formatDT(entry.created_at)}</span>} />
        <ProvRow icon={Clock} label="Approval status" value={<StatusPill status={entry.approval_status} />} />
        {approved_by_name && (
          <ProvRow icon={CheckCircle2} label="Actioned by" value={<span data-testid="review-approved-by">{approved_by_name}</span>} />
        )}
        {entry.linked_entry_id && (
          <ProvRow icon={Link2} label="Linked leg" value={<span className="cb-num" data-testid="review-linked-id">{entry.linked_entry_id.slice(0, 8)}…</span>} />
        )}
        <ProvRow icon={Calendar} label="Cycle window" value={
          cycle
            ? <span className="cb-num" data-testid="review-cycle-window">{cycle.start_date} — {cycle.end_date}<br /><span className="cb-muted" style={{ fontSize: '0.6875rem' }}>{cycle.type} · {cycle.status}</span></span>
            : <span className="cb-muted" data-testid="review-cycle-window">no cycle contains this date</span>
        } />
        {editHistory.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-3.5 h-3.5" style={{ color: 'var(--cb-muted)' }} />
              <span className="cb-muted" style={{ fontSize: '0.75rem' }}>Edit history ({editHistory.length})</span>
            </div>
            <ul style={{ fontSize: '0.75rem', paddingLeft: '1.4em' }} className="list-disc" data-testid="review-edit-history">
              {editHistory.slice(-3).map((h, i) => (
                <li key={i}><span className="cb-num">{formatDT(h.edited_at || h.timestamp)}</span> — {userMap[h.edited_by] || h.edited_by || '?'}: {h.edit_reason || h.reason || '—'}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function ProvRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'var(--cb-muted)' }} />
      <div className="flex-1">
        <div className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
        <div style={{ marginTop: '2px' }}>{value}</div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls =
    status === 'approved' ? 'cb-pill cb-pill--positive'
    : status === 'rejected' ? 'cb-pill cb-pill--negative'
    : 'cb-pill';
  return <span className={cls} data-testid="review-status-pill">{status}</span>;
}

const formatDT = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

/* ---------- Field renderers per entry type (mirror of create-form layout) ---------- */

function LedgerSingleFields({ entry, client, exchange }) {
  const isDeposit = entry.type === 'deposit';
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <LabeledReadonly label="Client" value={client ? `${client.client_code} — ${client.name}` : (entry.client_code || entry.client_id)} testid="review-field-client" />
        <LabeledReadonly label="Type" value={entry.type} testid="review-field-type" />
        <LabeledReadonly label="Currency" value={entry.currency} testid="review-field-currency" />
        <LabeledReadonly label="Amount" value={fmtNumber(entry.amount)} testid="review-field-amount" mono />
        {isDeposit && (
          <LabeledReadonly label="Deposit Method" value={entry.deposit_method || '—'} testid="review-field-deposit-method" />
        )}
        {!isDeposit && (
          <LabeledReadonly label="Withdrawal Source" value={entry.withdrawal_source || '—'} testid="review-field-withdrawal-source" />
        )}
        {entry.deposit_method === 'usdt_exchange' && entry.source_exchange && (
          <LabeledReadonly label="Source Exchange" value={exchange ? exchange.name : entry.source_exchange} testid="review-field-source-exchange" />
        )}
        <LabeledReadonly label="Date" value={entry.date} testid="review-field-date" mono />
        <LabeledReadonly label="Conversion Rate" value={fmtNumber(entry.conversion_rate)} testid="review-field-conversion-rate" mono />
        <div className="col-span-2 flex items-center gap-2" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
          <input type="checkbox" checked={!!entry.exclude_from_current_cycle} disabled readOnly data-testid="review-field-exclude" />
          <span>Exclude from current cycle</span>
        </div>
        {entry.notes && (
          <div className="col-span-2">
            <label className="form-label">Notes</label>
            <textarea readOnly disabled value={entry.notes} data-testid="review-field-notes" className={disabledInput} style={{ ...disabledInputStyle, minHeight: 60 }} />
          </div>
        )}
      </div>
    </>
  );
}

function DualCurrencyFields({ entry, sibling, client, partner }) {
  // Show BOTH legs as one unit — matches the create form's dual-currency mode
  const inrLeg = entry.currency === 'INR' ? entry : sibling;
  const usdtLeg = entry.currency === 'USDT' ? entry : sibling;
  return (
    <>
      <div className="cb-card" style={{ padding: 'var(--cb-space-4)', background: 'var(--cb-bone)', border: '1px dashed var(--cb-line)', marginBottom: 'var(--cb-space-4)' }}>
        <p className="cb-eyebrow" style={{ marginBottom: '6px' }}>Dual-Currency Entry (linked pair)</p>
        <p className="cb-muted" style={{ fontSize: '0.75rem' }}>Both legs shown together; approve/reject cascades to the sibling automatically.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <LabeledReadonly label="Client" value={client ? `${client.client_code} — ${client.name}` : entry.client_id} testid="review-field-client" />
        <LabeledReadonly label="Direction" value={entry.type} testid="review-field-direction" />
      </div>

      <div className="mt-4">
        <p className="cb-eyebrow" style={{ marginBottom: 'var(--cb-space-2)' }}>INR leg</p>
        <div className="grid grid-cols-2 gap-3" data-testid="review-inr-leg">
          <LabeledReadonly label="Amount (INR)" value={fmtNumber(inrLeg?.amount)} testid="review-inr-amount" mono />
          <LabeledReadonly label="Partner" value={partner ? partner.name || partner.username : (inrLeg?.partner_id || '—')} testid="review-inr-partner" />
          <LabeledReadonly label="Deposit method" value={inrLeg?.deposit_method || '—'} testid="review-inr-method" />
          <LabeledReadonly label="Approval status" value={inrLeg?.approval_status} testid="review-inr-status" />
        </div>
      </div>

      <div className="mt-4">
        <p className="cb-eyebrow" style={{ marginBottom: 'var(--cb-space-2)' }}>USDT leg</p>
        <div className="grid grid-cols-2 gap-3" data-testid="review-usdt-leg">
          <LabeledReadonly label="Amount (USDT)" value={fmtNumber(usdtLeg?.amount)} testid="review-usdt-amount" mono />
          <LabeledReadonly label="Internal Rate" value={fmtNumber(usdtLeg?.conversion_rate || inrLeg?.conversion_rate)} testid="review-internal-rate" mono />
          <LabeledReadonly label="Deposit method" value={usdtLeg?.deposit_method || '—'} testid="review-usdt-method" />
          <LabeledReadonly label="Approval status" value={usdtLeg?.approval_status} testid="review-usdt-status" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <LabeledReadonly label="Date" value={entry.date} testid="review-field-date" mono />
        <div className="flex items-center gap-2" style={{ fontSize: '0.875rem', marginTop: '20px' }}>
          <input type="checkbox" checked={!!entry.exclude_from_current_cycle} disabled readOnly data-testid="review-field-exclude" />
          <span>Exclude from current cycle</span>
        </div>
        {entry.notes && (
          <div className="col-span-2">
            <label className="form-label">Notes</label>
            <textarea readOnly disabled value={entry.notes} data-testid="review-field-notes" className={disabledInput} style={{ ...disabledInputStyle, minHeight: 60 }} />
          </div>
        )}
      </div>
    </>
  );
}

function ExpenseFields({ entry, partner }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <LabeledReadonly label="Category" value={entry.category} testid="review-field-category" />
      <LabeledReadonly label="Currency" value={entry.currency} testid="review-field-currency" />
      <LabeledReadonly label="Amount" value={fmtNumber(entry.amount)} testid="review-field-amount" mono />
      <LabeledReadonly label="Date" value={entry.date} testid="review-field-date" mono />
      <LabeledReadonly label="Payment Source" value={entry.payment_source || '—'} testid="review-field-payment-source" />
      {(entry.payment_source === 'cash_pool' || entry.partner_id) && (
        <LabeledReadonly label="Partner" value={partner ? partner.name || partner.username : (entry.partner_id || '—')} testid="review-field-partner" />
      )}
      {entry.description && (
        <div className="col-span-2">
          <label className="form-label">Description</label>
          <textarea readOnly disabled value={entry.description} data-testid="review-field-description" className={disabledInput} style={{ ...disabledInputStyle, minHeight: 60 }} />
        </div>
      )}
    </div>
  );
}

const fmtNumber = (n) => {
  if (n == null || n === '') return '—';
  const v = typeof n === 'number' ? n : parseFloat(n);
  if (Number.isNaN(v)) return String(n);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

/* ---------- Main dialog ---------- */

export default function ReviewEntryDialog({ open, entry, entryKind, onClose, onActioned, clients = [], users = [], exchanges = [], currentUserId = null }) {
  const [sibling, setSibling] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [selfApprovePrompt, setSelfApprovePrompt] = useState({ open: false, reason: '' });
  const [reqChangesPrompt, setReqChangesPrompt] = useState({ open: false, note: '' });

  // Build a userId -> username map for provenance display
  const userMap = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u.username || u.name || u.id;
    return m;
  }, [users]);

  const client = useMemo(
    () => clients.find(c => c.id === entry?.client_id),
    [clients, entry?.client_id]
  );
  const partner = useMemo(
    () => users.find(u => u.id === (entry?.partner_id || sibling?.partner_id)),
    [users, entry?.partner_id, sibling?.partner_id]
  );
  const exchange = useMemo(
    () => exchanges.find(e => e.id === entry?.source_exchange),
    [exchanges, entry?.source_exchange]
  );

  const isDual = entryKind === 'ledger' && !!entry?.linked_entry_id;
  const isExpense = entryKind === 'expense';

  // Fetch sibling (for dual-currency) + owning cycle. This is the ONLY network
  // fetch on dialog open. No live rates, no recomputes.
  useEffect(() => {
    if (!open || !entry) return;
    let cancelled = false;
    async function loadContext() {
      setLoading(true);
      try {
        // Sibling for dual-currency
        if (isDual) {
          try {
            // The ledger list endpoint by-client contains both legs. We reuse
            // whatever the parent Approvals page already fetched by locating
            // it in-memory if possible — otherwise a targeted lookup.
            const resp = await axiosInstance.get(`/ledger`, {
              params: { client_id: entry.client_id }
            });
            const sib = (resp.data || []).find(e => e.id === entry.linked_entry_id);
            if (!cancelled) setSibling(sib || null);
          } catch (e) { /* ignore — sibling info is best-effort */ }
        } else {
          setSibling(null);
        }
        // Cycle window
        try {
          const resp = await axiosInstance.get('/cycles');
          const dateStr = entry.date;
          const containing = (resp.data || []).find(c =>
            c.start_date && c.end_date && c.start_date <= dateStr && dateStr <= c.end_date
          );
          if (!cancelled) setCycle(containing || null);
        } catch (e) { /* non-fatal */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadContext();
    return () => { cancelled = true; };
  }, [open, entry, isDual]);

  if (!open || !entry) return null;

  const rate = parseFloat(entry.conversion_rate || sibling?.conversion_rate || 0);
  const rateOutOfBand = rate && (rate < RATE_MIN || rate > RATE_MAX);

  const disableActions = entry.approval_status !== 'pending' || acting;
  const isSelfApproval = !!currentUserId && entry.created_by === currentUserId;

  const doApprove = async () => {
    if (isSelfApproval) {
      // Prompt for reason first
      setSelfApprovePrompt({ open: true, reason: '' });
      return;
    }
    await doAction('approve');
  };

  const confirmSelfApprove = async () => {
    const reason = (selfApprovePrompt.reason || '').trim();
    if (!reason) { toast.error('A reason is required to self-approve'); return; }
    setSelfApprovePrompt({ open: false, reason: '' });
    await doAction('approve', { self_approval_reason: reason });
  };

  const doAction = async (action, body = null) => {
    setActing(true);
    try {
      const base = isExpense ? '/expenses' : '/ledger';
      await axiosInstance.post(`${base}/${entry.id}/${action}`, body || {});
      toast.success(`Entry ${action.replace('-',' ')}${body?.self_approval_reason ? ' (self, audit-logged)' : ''}`);
      onActioned?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActing(false);
    }
  };

  const openRequestChanges = () => setReqChangesPrompt({ open: true, note: '' });
  const submitRequestChanges = async () => {
    const note = (reqChangesPrompt.note || '').trim();
    if (!note) { toast.error('A note is required to request changes'); return; }
    setReqChangesPrompt({ open: false, note: '' });
    await doAction('request-changes', { note });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(11,11,10,0.55)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="review-entry-dialog"
    >
      <div className="cb-card" style={{ maxWidth: 1100, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 0 }}>
        <div className="flex items-center justify-between" style={{ padding: 'var(--cb-space-4) var(--cb-space-5)', borderBottom: '1px solid var(--cb-line)' }}>
          <div>
            <p className="cb-eyebrow">Review — {entryKind === 'expense' ? 'Expense' : (isDual ? 'Dual-Currency Ledger' : `${entry.type === 'deposit' ? 'Deposit' : 'Withdrawal'}`)}</p>
            <h2 className="cb-section-title" style={{ margin: '4px 0 0' }}>
              {entryKind === 'expense' ? entry.category : (client ? `${client.client_code} — ${client.name}` : entry.client_id?.slice(0,8))}
            </h2>
          </div>
          <button onClick={onClose} className="cb-btn-secondary" data-testid="review-close-button" aria-label="Close review">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid" style={{ gridTemplateColumns: '2fr 1fr', gap: 'var(--cb-space-4)', padding: 'var(--cb-space-5)' }}>
          {/* LEFT: read-only form mirror */}
          <div>
            {rateOutOfBand && (
              <div className="cb-notice" style={{ borderColor: 'var(--cb-negative)', marginBottom: 'var(--cb-space-4)' }} data-testid="review-rate-warning">
                <AlertTriangle className="w-4 h-4" style={{ color: 'var(--cb-negative)' }} />
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--cb-negative)' }}>Conversion rate {rate} is outside 60–130 INR/USDT</div>
                  <div className="cb-muted" style={{ fontSize: '0.75rem' }}>Warning only — you may still approve, but double-check the input.</div>
                </div>
              </div>
            )}

            {isExpense ? (
              <ExpenseFields entry={entry} partner={partner} />
            ) : isDual ? (
              <DualCurrencyFields entry={entry} sibling={sibling} client={client} partner={partner} />
            ) : (
              <LedgerSingleFields entry={entry} client={client} exchange={exchange} />
            )}
          </div>

          {/* RIGHT: provenance */}
          <div>
            <ProvenancePanel entry={entry} sibling={sibling} cycle={cycle} userMap={userMap} />
            {Array.isArray(entry.review_notes) && entry.review_notes.length > 0 && (
              <div className="cb-card mt-4" style={{ padding: 'var(--cb-space-4)' }} data-testid="review-notes-thread">
                <h3 className="cb-eyebrow" style={{ marginBottom: 'var(--cb-space-2)' }}>Review Thread</h3>
                <ul className="space-y-2" style={{ fontSize: '0.8125rem' }}>
                  {entry.review_notes.map((n, i) => (
                    <li key={i} style={{ borderLeft: '2px solid var(--cb-line)', paddingLeft: '8px' }}>
                      <div className="cb-muted" style={{ fontSize: '0.6875rem' }}>
                        <span className="cb-num">{formatDT(n.at)}</span> · {n.by} · <em>{n.action}</em>
                      </div>
                      <div style={{ marginTop: '2px', whiteSpace: 'pre-wrap' }}>{n.note}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2" style={{ padding: 'var(--cb-space-4) var(--cb-space-5)', borderTop: '1px solid var(--cb-line)', background: 'var(--cb-bone)' }}>
          <div className="cb-muted" style={{ fontSize: '0.75rem' }}>
            {entry.approval_status === 'pending'
              ? (loading ? 'Loading context…' : 'Approve or reject uses existing endpoints; dual-currency siblings are cascaded automatically.')
              : `This entry is already ${entry.approval_status}. Actions disabled.`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => doAction('reject')}
              disabled={disableActions}
              className="cb-btn-secondary cb-btn-secondary--negative"
              data-testid="review-reject-button"
            >
              <XCircle className="w-4 h-4" /> Reject
            </button>
            <button
              onClick={openRequestChanges}
              disabled={disableActions}
              className="cb-btn-secondary"
              data-testid="review-request-changes-button"
              title="Send back to creator with a note"
            >
              <MessageSquare className="w-4 h-4" /> Request changes
            </button>
            <button
              onClick={doApprove}
              disabled={disableActions}
              className="cb-btn-primary"
              data-testid="review-approve-button"
            >
              <CheckCircle2 className="w-4 h-4" /> {isSelfApproval ? 'Approve (self)…' : 'Approve'}
            </button>
          </div>
        </div>

        {/* Request-changes note prompt */}
        {reqChangesPrompt.open && (
          <div role="dialog" aria-modal="true"
            style={{ position:'fixed', inset:0, zIndex:1100, background:'rgba(11,11,10,0.65)',
                     display:'flex', alignItems:'center', justifyContent:'center', padding:'32px 16px' }}
            onClick={(e) => { if (e.target === e.currentTarget) setReqChangesPrompt({ open:false, note:'' }); }}
            data-testid="request-changes-prompt">
            <div className="cb-card" style={{ maxWidth: 480, width:'100%', padding: 'var(--cb-space-5)' }}>
              <p className="cb-eyebrow" style={{ marginBottom: 4 }}>Send back to finance</p>
              <h3 className="cb-section-title" style={{ margin:'0 0 8px' }}>Note required</h3>
              <p className="cb-muted" style={{ fontSize:'0.8125rem', marginBottom:'var(--cb-space-3)' }}>
                Explain what needs correcting. The note becomes a review-thread entry visible to the creator.
              </p>
              <textarea value={reqChangesPrompt.note}
                onChange={(e) => setReqChangesPrompt({ open:true, note: e.target.value })}
                placeholder="e.g. Deposit method should be bank_transfer, not cash"
                className="form-input" style={{ minHeight: 80, width:'100%' }}
                data-testid="request-changes-note-input"/>
              <div className="flex justify-end gap-2" style={{ marginTop:'var(--cb-space-3)' }}>
                <button className="cb-btn-secondary" onClick={() => setReqChangesPrompt({ open:false, note:'' })}
                  data-testid="request-changes-cancel">Cancel</button>
                <button className="cb-btn-primary" onClick={submitRequestChanges}
                  disabled={!reqChangesPrompt.note.trim() || acting}
                  data-testid="request-changes-confirm">
                  <MessageSquare className="w-4 h-4" /> Send back
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Self-approval reason prompt */}
        {selfApprovePrompt.open && (
          <div
            role="dialog" aria-modal="true"
            style={{ position:'fixed', inset:0, zIndex:1100, background:'rgba(11,11,10,0.65)',
                     display:'flex', alignItems:'center', justifyContent:'center', padding:'32px 16px' }}
            onClick={(e) => { if (e.target === e.currentTarget) setSelfApprovePrompt({ open:false, reason:'' }); }}
            data-testid="self-approve-prompt"
          >
            <div className="cb-card" style={{ maxWidth: 480, width:'100%', padding: 'var(--cb-space-5)' }}>
              <p className="cb-eyebrow" style={{ marginBottom: 4 }}>Self-approval override</p>
              <h3 className="cb-section-title" style={{ margin:'0 0 8px' }}>Reason required</h3>
              <p className="cb-muted" style={{ fontSize:'0.8125rem', marginBottom:'var(--cb-space-3)' }}>
                You created this entry. Provide a business reason to approve it yourself — it will be recorded on the entry and in the audit log.
              </p>
              <textarea
                value={selfApprovePrompt.reason}
                onChange={(e) => setSelfApprovePrompt({ open:true, reason: e.target.value })}
                placeholder="e.g. Sole admin on duty; entry verified by phone with partner"
                className="form-input"
                style={{ minHeight: 80, width:'100%' }}
                data-testid="self-approve-reason-input"
              />
              <div className="flex justify-end gap-2" style={{ marginTop:'var(--cb-space-3)' }}>
                <button className="cb-btn-secondary" onClick={() => setSelfApprovePrompt({ open:false, reason:'' })} data-testid="self-approve-cancel">Cancel</button>
                <button className="cb-btn-primary" onClick={confirmSelfApprove} disabled={!selfApprovePrompt.reason.trim() || acting} data-testid="self-approve-confirm">
                  <CheckCircle2 className="w-4 h-4" /> Confirm self-approval
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
